import React, { useEffect, useState } from "react";
import { Checkbox, Modal, Table, Spin } from "antd";
import styled from "styled-components";
import Button from "atoms/Button";
import moment from "moment";

const StyledButton = styled(Button)`
  background: #00685F !important;
  border-radius: 5px;
  color: #ffffff !important;
  width: 100px;
   &:hover {
	  color: #ffffff !important;
  }
   &:focus {
	  color: #ffffff !important;
  }
`;

const StyledButtonCancel = styled(Button)`
  background: #ffffff !important;
  border-radius: 5px;
  color: #000000!important;
  width: 100px;
  border-color: #000000!important;
  &:focus {
      color: #000000 !important;
  }
`;

export const ViewTimelogs = ({
	isModalVisible,
	setIsModalVisible,
	addTimelogs,
	setAddTimelogs,
	timelogsDataSource,
	isViewTimelogs,
	update,
	activeTimelogIds = [],
	userData,
}) => {
	const [selectedKeys, setSelectedKeys] = useState(new Set());
	const [resolvedData, setResolvedData] = useState([]);
	const [fetchingView, setFetchingView] = useState(false);
	const [expandedDescriptions, setExpandedDescriptions] = useState({});

	useEffect(() => {
		if (!isModalVisible) return;

		const unwrapped = (Array.isArray(timelogsDataSource) ? timelogsDataSource : []).map((t) => {
			// Already a flat object (from addTimelogs in view mode)
			if (t?.timelogId && typeof t.timelogId === "object") {
				return { ...t.timelogId };
			}
			// Flat record — pass through as-is (preserves status, timeSpent, etc.)
			return { ...t };  // ← was just `t`, now spreads to ensure it's a plain object
		});

		setResolvedData(unwrapped);
	}, [isModalVisible, timelogsDataSource]);

	useEffect(() => {
		if (!isModalVisible) {
			setSelectedKeys(new Set()); // ← reset on close
			return;
		}

		if (update && addTimelogs?.length > 0) {
			const ids = new Set(addTimelogs.map((t) => t?.timelogid || t?.id || t));
			setSelectedKeys(ids);
		} else {
			setSelectedKeys(new Set()); // reset on fresh open
		}
	}, [isModalVisible]); // ← trigger on modal visibility change

	const handleCheckboxChange = (e, key) => {
		setSelectedKeys((prev) => {
			const next = new Set(prev);
			e.target.checked ? next.add(key) : next.delete(key);
			return next;
		});
	};

	const timelogsData = resolvedData
		.map((record, index) => ({
			...record,
			key: record?.timelogid || record?.id || index,
		}))
		.filter(
			(record) =>
				!activeTimelogIds?.includes(record?.timelogid || record.key)
		);

	const handleSave = () => {
		const selectedRecords = timelogsData.filter((r) => selectedKeys.has(r.key));
		const mappedRecords = selectedRecords.map((item) => ({ ...item }));
		setAddTimelogs(mappedRecords);
		handleCancel();
	};

	const handleCancel = () => setIsModalVisible(false);

	const toggleDescription = (key) => {
		setExpandedDescriptions((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const columns = [
		!isViewTimelogs
			? {
				title: "Action",
				dataIndex: "action",
				key: "action",
				render: (_, record) => (
					<Checkbox
						checked={
							activeTimelogIds?.includes(record?.timelogid) ||
							selectedKeys.has(record?.timelogid || record.key)
						}
						onChange={(e) =>
							handleCheckboxChange(e, record?.timelogid || record.key)
						}
					// disabled={activeTimelogIds?.includes(record?.timelogid)}
					/>
				),
			}
			: null,
		{ title: "Matter", dataIndex: "matter", key: "matter" },
		{ title: "Client", dataIndex: "client", key: "client" },
		{ title: "Category", dataIndex: "category", key: "category" },
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			width: 350,
			render: (val, record) => {
				if (!val) return "–";

				const isExpanded = expandedDescriptions[record.key];
				const plainText = val.replace(/<[^>]+>/g, "");
				const shouldShowToggle = plainText.length > 120;

				return (
					<div>
						<DescriptionBox expanded={isExpanded}>
							<span dangerouslySetInnerHTML={{ __html: val }} />
						</DescriptionBox>

						{shouldShowToggle && (
							<span
								onClick={() => toggleDescription(record.key)}
								style={{
									color: "#397EF3",
									cursor: "pointer",
									fontWeight: 500,
									display: "inline-block",
									marginTop: 4,
								}}
							>
								{isExpanded ? "View Less" : "View More"}
							</span>
						)}
					</div>
				);
			},
		},

		{
			title: "Time Spent",
			dataIndex: "timeSpent",
			key: "timeSpent",
			render: (val) => (val ? `${val} hrs` : "–"),
		},
		{
			title: "Amount",
			dataIndex: "convertedamount",
			key: "convertedamount",
			render: (val) => (val ? `${val}` : "–"),
		},
		{ title: "Status", dataIndex: "status", key: "status" },
		{
			title: "Submitted By",
			dataIndex: "submittedBy",
			key: "submittedBy",
			render: (id) => {
				return userData?.find((user) => user.userid === id)?.fullname || "-";
			},
		},
		{
			title: "Date",
			dataIndex: "dateOfLog",
			key: "dateOfLog",
			render: (value) => (value ? moment(value).format("DD-MM-YYYY") : "–"),
		},
	].filter(Boolean);

	return (
		<StyledTimelogModal
			visible={isModalVisible}
			onCancel={handleCancel}
			footer={false}
		>
			<h6>{isViewTimelogs ? "Timelogs" : "Select Timelogs:"}</h6>
			<Spin spinning={fetchingView}>
				<Table
					dataSource={timelogsData}
					columns={columns}
					rowKey={(r) => r.key}
					pagination={false}
					scroll={{ x: 800, y: 500 }}
				/>
			</Spin>
			{!isViewTimelogs && (
				<div className="text-end mt-2">
					<StyledButtonCancel onClick={handleCancel}>Cancel</StyledButtonCancel>
					<StyledButton onClick={handleSave}>Save</StyledButton>
				</div>
			)}
		</StyledTimelogModal>
	);
};

const StyledTimelogModal = styled(Modal)`
  width: 1200px !important;
  z-index: 10;
  .ant-modal-body {
    overflow-y: auto;
    max-height: 85vh;
    padding-right: 16px;
  }
  ul.ant-pagination { display: none; }
  .ant-modal-close-x { display: none; }
`;

const DescriptionBox = styled.div`
  max-height: ${({ expanded }) => (expanded ? "60px" : "44px")};
  overflow-y: auto;
  white-space: normal;
  word-break: break-word;
  line-height: 1.5;
  padding-right: 6px;
`;